import posthog from 'posthog-js';

/**
 * Minimal PostHog analytics wrapper.
 *
 * Goals:
 * - Keep event names consistent and typed
 * - Avoid sprinkling raw `posthog.capture(...)` across the app
 * - No-ops safely when PostHog is not configured
 */

export type AnalyticsEvent =
  | 'user_signed_up'
  | 'user_logged_in'
  | 'onboarding_completed'
  | 'project_created'
  | 'quote_generated'
  | 'invite_sent'
  | 'invite_accepted'
  | 'upgrade_intent';

export type AnalyticsProps = Record<string, unknown>;

function isEnabled(): boolean {
  return Boolean(process.env.REACT_APP_POSTHOG_KEY);
}

function compactProps<T extends AnalyticsProps | undefined>(props: T): T {
  if (!props) return props;
  const out: AnalyticsProps = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out as T;
}

export function analyticsIdentify(distinctId: string, personProps?: AnalyticsProps): void {
  if (!isEnabled()) return;
  try {
    posthog.identify(distinctId, compactProps(personProps));
  } catch {
    // ignore analytics failures
  }
}

export function analyticsCapture(event: AnalyticsEvent, props?: AnalyticsProps): void {
  if (!isEnabled()) return;
  try {
    posthog.capture(event, compactProps(props));
  } catch {
    // ignore analytics failures
  }
}

export function bucketMoney(amount: number): string {
  if (!Number.isFinite(amount)) return 'unknown';
  if (amount <= 0) return '0';
  if (amount < 1000) return '0-1k';
  if (amount < 5000) return '1k-5k';
  if (amount < 10000) return '5k-10k';
  if (amount < 50000) return '10k-50k';
  return '50k+';
}
